import { useQuery, useQueryClient } from "@tanstack/react-query";
import { callWithAuth, getDictionaryStats } from "@/api/Api";
import { DICTIONARY_STATS_QUERY_KEY } from "@/query_keys";

/**
 * Custom hook for managing dictionary statistics
 * @returns Object with dictionary stats and methods to update them
 */
export const useDictionaryStats = () => {
  const queryClient = useQueryClient();

  // Query for fetching dictionary stats
  const query = useQuery({
    queryKey: [DICTIONARY_STATS_QUERY_KEY],
    queryFn: () => callWithAuth(getDictionaryStats),
  });

  /**
   * Optimistically update known word count
   * @param amount Number of known words to add (can be negative for subtraction)
   */
  const addKnownWords = (amount: number) => {
    if (amount === 0) return;

    queryClient.setQueryData([DICTIONARY_STATS_QUERY_KEY], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        knownWordCount: (oldData.knownWordCount || 0) + amount,
        knownCount: (oldData.knownCount || 0) + amount,
      };
    });
  };

  /**
   * Optimistically update learning word count
   * @param amount Number of learning words to add (can be negative for subtraction)
   */
  const addLearningWords = (amount: number) => {
    if (amount === 0) return;

    queryClient.setQueryData([DICTIONARY_STATS_QUERY_KEY], (oldData: any) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        learningWordCount: (oldData.learningWordCount || 0) + amount,
        learningCount: (oldData.learningCount || 0) + amount,
      };
    });
  };

  const learningWordCount = query.data?.learningWordCount || 0;
  const knownWordCount = query.data?.knownWordCount || 0;
  const totalWordCount = knownWordCount + learningWordCount;
  return {
    knownWordCount: knownWordCount,
    learningWordCount: learningWordCount,
    totalWordCount: totalWordCount,
    isLoading: query.isLoading,
    error: query.error,
    addKnownWords,
    addLearningWords,
  };
};
